/**
 * The monitoring workflow's Needs you tab (DOP-480, restyled DOP-625).
 *
 * What stands between this quarter and a lock, one card per borrower, each
 * with the reason in words and the one thing to do: Start checks for open
 * checks or a breach, Run for a package that is in and not run, and — for a
 * package that is late — nothing to click, just what to do once it lands.
 *
 * It is a list of LINKS, not the check editor: resolving a check writes a
 * figure, which needs the whole run the board does not carry. The board does
 * carry `run.gaps_open`, the package and the covenant results, which is
 * enough to say who is blocked and why, and send the reviewer to the quarter
 * where the check is answered. The same `borrowerStanding` as the board
 * decides who is listed, so the tab and the board's tile always agree.
 */

import React from "react";
import { Link } from "react-router-dom";
import type { MonitoringBoardRow, MonitoringPeriod } from "../../types/monitoring";
import { borrowerStanding, needsYou, worstCovenant } from "./borrowerStanding";
import { formatFigure } from "./monitoringFormat";
import { runPath } from "./PeriodBoard";

/** Rows a reviewer still has to act on, in the board's order. */
export const rowsNeedingReview = (
	rows: readonly MonitoringBoardRow[],
	period: Pick<MonitoringPeriod, "package_due">,
): MonitoringBoardRow[] => rows.filter((row) => needsYou(borrowerStanding(row, period)));

/** How many borrowers need a person this quarter: the tab's badge. */
export const reviewCount = (
	rows: readonly MonitoringBoardRow[],
	period: Pick<MonitoringPeriod, "package_due">,
): number => rowsNeedingReview(rows, period).length;

const BUTTON =
	"inline-flex h-9 items-center whitespace-nowrap rounded-lg border px-3.5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] disabled:opacity-50";

export interface NeedsReviewListProps {
	rows: readonly MonitoringBoardRow[];
	period: MonitoringPeriod;
	onRunBorrower?: (borrowerId: string) => void;
	runningBorrowerIds?: ReadonlySet<string>;
}

const NeedsReviewList: React.FC<NeedsReviewListProps> = ({
	rows,
	period,
	onRunBorrower,
	runningBorrowerIds,
}) => {
	const pending = rowsNeedingReview(rows, period);

	if (pending.length === 0) {
		return (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
				Nothing needs you in {period.label}: every borrower has a run with no open checks.
			</p>
		);
	}

	return (
		<section className="space-y-3.5">
			<p className="text-sm text-[#5B6472]">
				Each borrower's quarter can be locked once nothing here is open.
			</p>
			{pending.map((row) => {
				const standing = borrowerStanding(row, period);
				const run = row.run;
				const worst = worstCovenant(row);
				const checks = run?.gaps_open ?? 0;
				const summary = [
					checks > 0 ? `${checks} ${checks === 1 ? "check" : "checks"}` : null,
					worst?.result === "breach" ? "1 covenant breach" : null,
				]
					.filter(Boolean)
					.join(" · ");
				return (
					<section
						key={row.borrower.public_id}
						aria-label={row.borrower.name}
						className="rounded-xl border border-gray-200 bg-white"
						data-testid={`needs-you-${row.borrower.public_id}`}
					>
						<div className="flex items-center gap-3 px-[18px] py-3.5">
							<div className="min-w-0 flex-1">
								<p className="text-[15px] font-semibold text-[#111111]">{row.borrower.name}</p>
								<p
									className={`mt-0.5 text-xs ${standing.kind === "late" ? "text-[#b42318]" : "text-[#6B7280]"}`}
								>
									{standing.kind === "late" || standing.kind === "ready_to_run" || standing.kind === "failed"
										? [standing.label, standing.detail].filter(Boolean).join(" · ")
										: summary}
								</p>
							</div>
							{run && (standing.kind === "checks" || standing.kind === "breach") && (
								<Link
									to={runPath(run.public_id)}
									className={`${BUTTON} border-[#004C4B] bg-[#004C4B] font-semibold text-white hover:bg-[#003a3a]`}
								>
									{checks > 0 ? (checks === 1 ? "Start check" : "Start checks") : "Review"}
								</Link>
							)}
							{(standing.kind === "ready_to_run" || standing.kind === "failed") && onRunBorrower && (
								<button
									type="button"
									disabled={runningBorrowerIds?.has(row.borrower.public_id)}
									onClick={() => onRunBorrower(row.borrower.public_id)}
									className={`${BUTTON} border-[#99CFCD] bg-white text-[#004C4B] hover:bg-[#f6fbfb]`}
								>
									{runningBorrowerIds?.has(row.borrower.public_id)
										? "Starting…"
										: standing.kind === "failed"
											? "Try again"
											: "Run"}
								</button>
							)}
							{standing.kind === "late" && (
								<span className="max-w-[260px] text-right text-xs text-[#6B7280]">
									Run it once the package is in the Deal Room.
								</span>
							)}
						</div>
						{checks > 0 && (
							<div className="flex items-center gap-2.5 border-t border-[#f1f2f4] px-[18px] py-2.5 text-[13px] text-[#3f4247]">
								<span className="w-44 text-[11px] font-bold uppercase tracking-[0.06em] text-[#b54708]">
									Not in the package
								</span>
								{checks === 1 ? "1 figure is" : `${checks} figures are`} missing; answer or accept each
								one before the lock.
							</div>
						)}
						{worst?.result === "breach" && worst.actual !== null && worst.required !== null && (
							<div className="flex items-center gap-2.5 border-t border-[#f1f2f4] bg-[#fffbfa] px-[18px] py-2.5 text-[13px] text-[#3f4247]">
								<span className="w-44 text-[11px] font-bold uppercase tracking-[0.06em] text-[#b42318]">
									Breach
								</span>
								{worst.label} {formatFigure(worst.actual, worst.unit)} against a limit of{" "}
								{worst.operator === "<=" ? "≤" : "≥"} {formatFigure(worst.required, worst.unit)}. It
								does not stop a lock; the lock records it.
							</div>
						)}
					</section>
				);
			})}
		</section>
	);
};

export default NeedsReviewList;
